import React, { Component, ErrorInfo, ReactNode } from 'react';
import './common.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class NavigationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('NavigationErrorBoundary caught error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // For navigation errors, we want to be more conservative and not auto-retry
    // since navigation issues can cause infinite loops
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleGoHome = () => {
    // Navigate to a safe route
    window.location.href = '/';
  };

  private isNavigationError(error: Error): boolean {
    const errorMessage = error.message.toLowerCase();
    const errorStack = error.stack?.toLowerCase() || '';
    
    const navigationPatterns = [
      'navigation',
      'router',
      'route',
      'history',
      'location',
      'redirect',
      'navigate',
      'link',
    ];

    return navigationPatterns.some(pattern => 
      errorMessage.includes(pattern) || errorStack.includes(pattern)
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isNavError = this.state.error ? this.isNavigationError(this.state.error) : false;

      return (
        <div className="error-display error-display-warning">
          <div className="error-display-content">
            <div className="error-display-icon">⚠️</div>
            <div className="error-display-text">
              <h3 className="error-display-title">
                {isNavError ? 'Errore di navigazione' : 'Errore nel menu'}
              </h3>
              <p className="error-display-message">
                {isNavError 
                  ? 'Si è verificato un problema con la navigazione. Puoi tornare alla home page o ricaricare la pagina.'
                  : 'Si è verificato un problema con il menu di navigazione. La funzionalità principale dovrebbe ancora funzionare.'
                }
              </p>
            </div>
          </div>
          
          <div className="error-display-actions">
            <button 
              onClick={this.handleGoHome}
              className="btn btn-primary"
            >
              Vai alla home
            </button>
            <button 
              onClick={this.handleRetry}
              className="btn btn-secondary"
            >
              Riprova
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="btn btn-outline"
            >
              Ricarica pagina
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="error-details" style={{ marginTop: '1rem' }}>
              <summary>Dettagli errore (solo sviluppo)</summary>
              <pre className="error-stack">
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default NavigationErrorBoundary;